<template>
    <!-- open-on-focus -->
  <v-menu
    v-model="isMenuOpen"
    offset-y
    left
    nudge-bottom="14"
    min-width="230"
    :close-on-content-click="false"
    content-class="user-profile-menu-content"
  >
    <template v-slot:activator="{ on, attrs }">
      <!-- the navigation across menu options is not available with tab but with the arrows keys -->
    <div tabindex="0" v-bind="attrs" v-on="on" class="ms-3 tabindex-handler" :aria-label="$t('common.user_menu')" >
      <v-badge
        bottom
        color="success"
        overlap
        offset-x="12"
        offset-y="12"
        class=""
        dot
      >
        <v-avatar
          size="40px"
          :color="user_data && user_data.photo ? '' : 'primary'"
          :class="user_data && user_data.photo ? '' : 'v-avatar-light-bg primary--text'"
        >
          <v-img v-if="user_data && user_data.photo" :src="user_data.photo"></v-img>
          <span v-else class="text-sm font-weight-semibold">{{ avatarInitial }}</span>
        </v-avatar>
      </v-badge>
    </div>
    </template>
    <v-list>
      <div class="pb-3 pt-2" v-if="user_data">
        <v-badge
          bottom
          color="success"
          overlap
          offset-x="12"
          offset-y="12"
          class="ms-4"
          dot
        >
          <v-avatar
            size="40px"
            :color="user_data.photo ? '' : 'primary'"
            :class="user_data.photo ? '' : 'v-avatar-light-bg primary--text'"
          >
            <v-img v-if="user_data.photo" :src="user_data.photo"></v-img>
            <span v-else class="text-sm font-weight-semibold">{{ avatarInitial }}</span>
          </v-avatar>
        </v-badge>
        <div
          class="d-inline-flex flex-column justify-center ms-3"
          style="vertical-align:middle"
        >
          <span class="text--primary font-weight-semibold mb-n1">
            {{ user_data.username }}
          </span>
          <small class="text--disabled text-capitalize">{{ roleParser(user_data.role) }}</small>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Profile -->
      <router-link
        to="/profile"
        class="d-flex align-center text-decoration-none"
        @click.native="isMenuOpen = false"
      >
        <v-list-item href="#">
          <v-list-item-icon class="me-2">
            <v-icon size="22">
              {{ icons.mdiAccountOutline }}
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{$t('common.profile')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </router-link>

      <v-divider class="my-2"></v-divider>

      <!-- Theme -->
      <v-list-item
        :aria-label="$t('common.change_theme')"
        @click="isDark = !isDark"
      >
        <v-list-item-icon class="me-2">
          <v-icon size="22">
            {{ isDark ? icons.mdiWeatherSunny : icons.mdiWeatherNight }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ isDark ? $t('common.light_theme') : $t('common.dark_theme') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <!-- Language -->
      <v-list-group
        v-model="isLanguageGroupOpen"
        :aria-label="$t('common.change_language')"
        no-action
      >
        <template v-slot:activator>
          <v-list-item-icon class="me-2">
            <v-img
              :src="require(`@/assets/images/flags/${$i18n.locale}.png`)"
              :alt="$i18n.locale"
              height="14px"
              width="22px"
              class="mt-1"
            ></v-img>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t(locales.find(l => l.locale === $i18n.locale).title) }}</v-list-item-title>
          </v-list-item-content>
        </template>

        <v-list-item-group
          :value="$i18n.locale"
          @change="updateActiveLocale"
        >
          <v-list-item
            v-for="locale in locales"
            :key="locale.locale"
            :value="locale.locale"
          >
            <v-list-item-icon class="me-2">
              <v-img
                :src="locale.img"
                :alt="locale.locale"
                height="14px"
                width="22px"
                class="mt-1"
              ></v-img>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t(locale.title) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list-group>

      <v-divider class="my-2"></v-divider>

      <!-- Logout -->
      <v-list-item @click="logoutUser">
        <v-list-item-icon class="me-2">
          <v-icon size="22">
            {{ icons.mdiLogoutVariant }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{$t('common.logout')}}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import {
  mdiAccountOutline,
  mdiEmailOutline,
  mdiCheckboxMarkedOutline,
  mdiChatOutline,
  mdiCogOutline,
  mdiCurrencyUsd,
  mdiHelpCircleOutline,
  mdiLogoutVariant,
  mdiWeatherNight,
  mdiWeatherSunny,
} from '@mdi/js'
import { ref } from '@vue/composition-api'
import { loadLanguageAsync } from '@/plugins/i18n'
import useAppConfig from '@core/@app-config/useAppConfig'

export default {
  methods: {
    async logoutUser() {
      const refreshToken = this.$store.getters['session/refresh']()

      // Blacklist the refresh token server-side; if this fails (e.g. offline)
      // still clear the local session so the user is logged out either way.
      if (refreshToken) {
        try {
          await this.$api.logoutUser(refreshToken)
        } catch (e) {
          // ignore — local logout proceeds regardless
        }
      }

      this.$store.dispatch('session/logoutUser')
      this.$router.push('/login')
    },
    roleParser(role) {
      const roles = {
        0: this.$t('auth_users.role_admin'),
				1: this.$t('auth_users.role_country'),
				2: this.$t('auth_users.role_regional'),
				3: this.$t('auth_users.role_school'),
        4: this.$t('auth_users.role_auditor'),
      }
      return roles[role]
    }
  },
  computed: {
    user_data() { return this.$store.getters['session/userData']() },
    avatarInitial() {
      const name = this.user_data?.name || this.user_data?.username || ''
      return name.charAt(0).toUpperCase()
    },
  },
  setup() {
    const { isDark } = useAppConfig()

    const isMenuOpen = ref(false)
    const isLanguageGroupOpen = ref(false)

    const locales = [
      {
        title: 'common.english',
        img: require('@/assets/images/flags/en.png'),
        locale: 'en',
      },
      {
        title: 'common.portuguese',
        img: require('@/assets/images/flags/pt.png'),
        locale: 'pt',
      },
      {
        title: 'common.spanish',
        img: require('@/assets/images/flags/es.png'),
        locale: 'es',
      },
      {
        title: 'common.vietnamese',
        img: require('@/assets/images/flags/vi.png'),
        locale: 'vi',
      },
      {
        title: 'common.hindi',
        img: require('@/assets/images/flags/hi.png'),
        locale: 'hi',
      },
      {
        title: 'common.indonesia',
        img: require('@/assets/images/flags/id.png'),
        locale: 'id',
      },
    ]

    const updateActiveLocale = locale => {
      // v-list-item-group emits undefined when the active item is clicked again
      if (!locale) return

      sessionStorage.setItem('locale', locale)
      loadLanguageAsync(locale)
      isLanguageGroupOpen.value = false
      isMenuOpen.value = false
    }

    return {
      isDark,
      isMenuOpen,
      isLanguageGroupOpen,
      locales,
      updateActiveLocale,

      icons: {
        mdiAccountOutline,
        mdiEmailOutline,
        mdiCheckboxMarkedOutline,
        mdiChatOutline,
        mdiCogOutline,
        mdiCurrencyUsd,
        mdiHelpCircleOutline,
        mdiLogoutVariant,
        mdiWeatherNight,
        mdiWeatherSunny,
      },
    }
  },
}
</script>

<style lang="scss">
.user-profile-menu-content {
  .v-list-item {
    min-height: 2.5rem !important;
  }
}
</style>
